import React, { useState, useEffect, useRef, ReactNode } from 'react';
import { CustomVizHookStatus } from 'apps/sigma2-ui/src/lib/custom-viz/types';
import { Spinner } from 'apps/sigma2-ui/src/components/ui/Spinner';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { IconInformationSvg } from 'apps/sigma2-ui/src/components/svg/IconInformation';
import { useTouchableTooltipCollection } from 'apps/sigma2-ui/src/lib/hooks/useTouchableTooltipCollection';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { IconGreenCheckCutoutSvg } from 'apps/sigma2-ui/src/components/svg/IconGreenCheckCutout';
import cn from 'classnames';

const DataRefreshIndicatorContainer = ({
  children,
  heightClass,
}: {
  children: ReactNode;
  heightClass?: string;
}) => {
  const computedHeightClass = heightClass ?? 'h-[39px]';
  return (
    <div
      className={cn(
        `inline-flex flex-row items-center gap-2 text-nowrap data-refresh-indicator ${computedHeightClass}`,
      )}
    >
      {children}
    </div>
  );
};

const DataRefreshIndicatorLoading = ({
  heightClass,
}: {
  heightClass?: string;
}) => {
  const language = useLanguage();
  const tooltipKey = 'data-refresh-info' as const;
  const { createTouchTarget, renderTooltip } = useTouchableTooltipCollection({
    items: [
      {
        key: tooltipKey,
      },
    ],
    tooltipFn: () => {
      return (
        <div className="flex flex-row max-w-[256px]">
          <S2Typography
            variant="body-6"
            translatedContent={language.dataRefreshIndicatorTooltip()}
            textColorVariant="gray-900"
          />
        </div>
      );
    },
  });

  return (
    <DataRefreshIndicatorContainer heightClass={heightClass}>
      <Spinner size={12} textColor="text-royal-700" />
      <S2Typography
        as="span"
        translatedContent={language.dataRefreshIndicatorRefreshing()}
        variant={'body-7'}
        textColorVariant="gray-800"
      />
      <div className="text-gray-600">
        {createTouchTarget({
          key: tooltipKey,
          children: <IconInformationSvg svg={{ className: 'w-4 h-4' }} />,
        })}
      </div>
      {renderTooltip()}
    </DataRefreshIndicatorContainer>
  );
};

const DataRefreshIndicatorUpdatedAt = ({
  createdAt,
  heightClass,
}: {
  createdAt: number;
  heightClass?: string;
}) => {
  const language = useLanguage();

  return (
    <DataRefreshIndicatorContainer heightClass={heightClass}>
      <S2Typography
        as="span"
        translatedContent={language.dataRefreshIndicatorUpdatedAt({
          createdAt,
        })}
        variant={'body-7'}
        textColorVariant="gray-800"
      />
    </DataRefreshIndicatorContainer>
  );
};

const DataRefreshIndicatorComplete = ({
  heightClass,
}: {
  heightClass?: string;
}) => {
  const language = useLanguage();

  return (
    <DataRefreshIndicatorContainer heightClass={heightClass}>
      <IconGreenCheckCutoutSvg svg={{ className: 'w-4 h-4' }} />
      <S2Typography
        as="span"
        translatedContent={language.dataRefreshIndicatorComplete()}
        variant={'body-7'}
        textColorVariant="gray-800"
      />
    </DataRefreshIndicatorContainer>
  );
};

export const DataRefreshIndicator = ({
  status,
  resultCreatedAt,
  heightClass,
}: {
  status: CustomVizHookStatus;
  resultCreatedAt: number | null;
  heightClass?: string;
}) => {
  const [showComplete, setShowComplete] = useState(false);
  const wasLoading = useRef(false);

  // Detect loading → updated transition
  useEffect(() => {
    const isLoading = status === 'loading-viz-with-stale-result';

    // Track if we were previously loading
    if (isLoading) {
      wasLoading.current = true;
    }

    // Transition: loading → updated
    if (wasLoading.current && !isLoading && status === 'waiting-for-user') {
      wasLoading.current = false;
      setShowComplete(true);

      // Hide after 7 seconds
      const timer = setTimeout(() => {
        setShowComplete(false);
      }, 7000);

      return () => clearTimeout(timer);
    }
  }, [status]);

  // No need to show the data refresh UI if we don't at least have a stale result.
  if (
    status === 'initialization' ||
    status === 'loading-dropdowns' ||
    status === 'loading-viz' ||
    status === 'fatal-error'
  ) {
    return <></>;
  }

  // We might have a result, but we don't know when it was created.
  if (status === 'waiting-for-user' && resultCreatedAt === null) {
    return <></>;
  }

  if (showComplete) {
    return <DataRefreshIndicatorComplete heightClass={heightClass} />;
  }

  if (status === 'loading-viz-with-stale-result') {
    return <DataRefreshIndicatorLoading heightClass={heightClass} />;
  }

  if (resultCreatedAt !== null) {
    return (
      <DataRefreshIndicatorUpdatedAt
        createdAt={resultCreatedAt}
        heightClass={heightClass}
      />
    );
  }

  // We need to know when a result was created to display it.
  return <></>;
};
